import React, { useEffect, useState, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from './axiosConfig';
import UIIcon from './UIIcon';
import './Sidebar.css';
import logo from './assets/logo.png';

const POLL_MS = 60_000;

const Sidebar = ({ handleLogout, isOpen = false, onClose = () => {}, currentUser, userLoading }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [hasNewDSI, setHasNewDSI] = useState(false);
  const pollRef = useRef(null);
  const canViewReporting = !!currentUser?.permissions?.reporting;

  const logoutHandler = () => {
    handleLogout?.();
    navigate('/login');
  };

  const checkNewDSI = async () => {
    try {
      const res = await axios.get('reports/new-dsi/', {
        params: { scan: 1, since_hours: 168, scope: 'mine' },
      });
      const arr = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.results)
        ? res.data.results
        : [];
      setHasNewDSI(arr.length > 0);
    } catch {
      // ignore transient errors
    }
  };

  useEffect(() => {
    checkNewDSI();
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(checkNewDSI, POLL_MS);
    const onVis = () => {
      if (document.visibilityState === 'visible') checkNewDSI();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  const displayName = [currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(' ') || currentUser?.username || t('app.user');
  const initials = ((currentUser?.first_name?.[0] || currentUser?.username?.[0] || 'U') + (currentUser?.last_name?.[0] || '')).toUpperCase();
  const locationCount = currentUser?.locations?.length || 0;

  const navClass = ({ isActive }) => `nav-link ${isActive ? 'active' : ''}`;

  return (
    <>
      <div className={`sidebar-backdrop ${isOpen ? 'show' : ''}`} onClick={onClose} />
      <aside className={`sidebar ${isOpen ? 'is-open' : ''}`} aria-label={t('app.primaryNavigation')}>
        <div className="sidebar-inner">
          <div className="sidebar-header">
            <div className="sidebar-brandmark"><img src={logo} alt={t('app.companyLogoAlt')} className="sidebar-logo" /></div>
            <div className="brand">
              <span className="brand-title">{t('app.brandTitle')}</span>
              <span className="brand-sub">{t('app.brandSub')}</span>
            </div>
            <button className="sidebar-close" aria-label={t('nav.closeSidebar', { defaultValue: 'Close navigation' })} onClick={onClose}>×</button>
          </div>

          <nav className="sidebar-nav">
            <div className="nav-section-label">{t('nav.workspace')}</div>
            <ul className="sidebar-menu">
              <li>
                <NavLink to="/dashboard/patients" end className={navClass} onClick={onClose}>
                  <span className="nav-icon"><UIIcon name="patients" size={19} /></span>
                  <span className="nav-copy"><span className="nav-label">{t('nav.patients', { defaultValue: 'Patients' })}</span><small>{t('nav.patientWorkspace')}</small></span>
                  <UIIcon name="chevronRight" size={15} className="nav-chevron" />
                </NavLink>
              </li>

              {!userLoading && canViewReporting && (
                <li>
                  <NavLink to="/dashboard/reporting" className={navClass} onClick={onClose}>
                    <span className="nav-icon"><UIIcon name="reporting" size={19} /></span>
                    <span className="nav-copy"><span className="nav-label">{t('nav.reporting', { defaultValue: 'Reporting' })}</span><small>{t('nav.reportingSub')}</small></span>
                    <UIIcon name="chevronRight" size={15} className="nav-chevron" />
                  </NavLink>
                </li>
              )}

              <li>
                <NavLink to="/dashboard/reports/new-dsi" className={navClass} onClick={onClose}>
                  <span className="nav-icon"><UIIcon name="dsi" size={19} /></span>
                  <span className="nav-copy">
                    <span className="nav-label">
                      {t('nav.newDSIReports', { defaultValue: 'New DSI Reports' })}
                      {hasNewDSI && <span className="nav-badge-alert" title={t('nav.newReportsAvailable', { defaultValue: 'New reports available' })}>!</span>}
                    </span>
                    <small>{t('nav.analysisReturns')}</small>
                  </span>
                  <UIIcon name="chevronRight" size={15} className="nav-chevron" />
                </NavLink>
              </li>
            </ul>

            <div className="nav-section-label nav-section-secondary">{t('nav.resources')}</div>
            <ul className="sidebar-menu">
              <li>
                <NavLink to="/dashboard/help-training" className={navClass} onClick={onClose}>
                  <span className="nav-icon"><UIIcon name="bookOpen" size={19} /></span>
                  <span className="nav-copy">
                    <span className="nav-label">{t('nav.helpTraining')}</span>
                    <small>{t('nav.helpTrainingSub')}</small>
                  </span>
                  <UIIcon name="chevronRight" size={15} className="nav-chevron" />
                </NavLink>
              </li>
            </ul>
          </nav>

          <div className="sidebar-footer">
            <div className="sidebar-user-card">
              <div className="sidebar-avatar">{initials}</div>
              <div className="sidebar-user-copy">
                <strong>{userLoading ? t('common.loading') : displayName}</strong>
                <span>{currentUser?.is_superuser ? t('app.superuser') : t(`role.${currentUser?.role || 'distributor'}`, { defaultValue: (currentUser?.role || t('app.user')).replaceAll('_', ' ') })}</span>
                {!userLoading && <small>{t('sidebar.clinicsAssigned', { count: locationCount })}</small>}
              </div>
            </div>
            <button className="sidebar-logout" onClick={logoutHandler}>
              <UIIcon name="logout" size={18} />
              <span>{t('logout', { defaultValue: 'Sign out' })}</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
